import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Bell, Check, Globe, LogOut, Menu, SunMoon } from 'lucide-react'
import type { User } from '@/models/user.model'
import { useLayoutContext } from '@/context/layout.context'
import { useAuth } from '@/hooks/use-auth'
import { useTheme } from '@/hooks/use-theme'
import { THEMES, THEME_LABELS, type Theme } from '@/constants'
import { cn } from '@/utils'
import { Breadcrumb } from './Breadcrumb'

// ─── Shared style constants ───────────────────────────────────────────────────

const contentCls =
  'z-50 min-w-[200px] overflow-hidden rounded-xl border border-border bg-card p-1 shadow-lg outline-none'

const itemCls =
  'flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground outline-none transition-colors hover:bg-accent focus:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50'

const labelCls = 'px-3 py-1.5 text-xs font-medium text-muted-foreground'

const separatorCls = 'my-1 h-px bg-border'

const iconBtnCls =
  'flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background'

// ─── Notifications ────────────────────────────────────────────────────────────

interface Notification {
  id: string
  title: string
  description: string
  time: string
  read: boolean
}

const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: '1',
    title: 'Nuevo usuario registrado',
    description: 'admin@empresa.com se unió a la plataforma',
    time: 'Hace 5 min',
    read: false,
  },
  {
    id: '2',
    title: 'Reporte generado',
    description: 'El reporte mensual está listo para descargar',
    time: 'Hace 1 hora',
    read: false,
  },
  {
    id: '3',
    title: 'Mantenimiento programado',
    description: 'El sistema estará en mantenimiento el viernes',
    time: 'Ayer',
    read: true,
  },
]

function NotificationsMenu() {
  const unreadCount = MOCK_NOTIFICATIONS.filter((n) => !n.read).length

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={iconBtnCls}
          aria-label={`Notificaciones${unreadCount > 0 ? `, ${String(unreadCount)} sin leer` : ''}`}
        >
          <div className="relative">
            <Bell className="h-4 w-4" aria-hidden="true" />
            {unreadCount > 0 && (
              <span
                className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-[9px] font-bold leading-none text-destructive-foreground"
                aria-hidden="true"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={8} className={cn(contentCls, 'w-80')}>
          <DropdownMenu.Label className={labelCls}>Notificaciones</DropdownMenu.Label>
          <DropdownMenu.Separator className={separatorCls} />

          {MOCK_NOTIFICATIONS.map((n) => (
            <DropdownMenu.Item key={n.id} className={cn(itemCls, 'flex-col items-start gap-0.5')}>
              <div className="flex w-full items-center justify-between gap-2">
                <span className={cn('text-sm font-medium', n.read && 'font-normal text-muted-foreground')}>
                  {n.title}
                </span>
                {!n.read && (
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                )}
              </div>
              <span className="text-xs text-muted-foreground">{n.description}</span>
              <span className="text-xs text-muted-foreground">{n.time}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

// ─── Theme picker ─────────────────────────────────────────────────────────────

function ThemeMenu() {
  const { theme, setTheme } = useTheme()

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button" className={iconBtnCls} aria-label="Cambiar tema">
          <SunMoon className="h-4 w-4" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={8} className={contentCls}>
          <DropdownMenu.Label className={labelCls}>Tema</DropdownMenu.Label>
          <DropdownMenu.Separator className={separatorCls} />

          <DropdownMenu.RadioGroup
            value={theme}
            onValueChange={(value) => setTheme(value as Theme)}
          >
            {THEMES.map((t) => (
              <DropdownMenu.RadioItem
                key={t}
                value={t}
                className="relative flex cursor-pointer select-none items-center rounded-lg py-2 pl-8 pr-3 text-sm text-foreground outline-none transition-colors hover:bg-accent focus:bg-accent"
              >
                <DropdownMenu.ItemIndicator className="absolute left-2.5 flex items-center">
                  <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                </DropdownMenu.ItemIndicator>
                {THEME_LABELS[t]}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

// ─── User menu ────────────────────────────────────────────────────────────────

function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/)
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
  }
  return fullName.slice(0, 2).toUpperCase()
}

interface UserMenuProps {
  user: User | null
  onLogout: () => void
}

function UserMenu({ user, onLogout }: UserMenuProps) {
  const initials = user ? getInitials(user.fullName) : '?'

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
          aria-label="Menú de usuario"
        >
          {initials}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={8} className={contentCls}>
          {/* User info — not interactive */}
          <div className="px-3 py-2">
            <p className="text-sm font-medium text-foreground">{user?.fullName ?? 'Usuario'}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            <span className="mt-1 inline-block rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-medium capitalize text-secondary-foreground">
              {user?.role}
            </span>
          </div>

          <DropdownMenu.Separator className={separatorCls} />

          <DropdownMenu.Item
            className={cn(
              itemCls,
              'text-destructive hover:bg-destructive/10 focus:bg-destructive/10',
            )}
            onSelect={onLogout}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Cerrar sesión
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

// ─── Header ───────────────────────────────────────────────────────────────────

export function Header() {
  const { toggleMobileOpen } = useLayoutContext()
  const { user, logout } = useAuth()

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card px-4 lg:px-6">
      {/* Mobile sidebar toggle */}
      <button
        type="button"
        onClick={toggleMobileOpen}
        className={cn(iconBtnCls, 'lg:hidden')}
        aria-label="Abrir menú de navegación"
      >
        <Menu className="h-5 w-5" aria-hidden="true" />
      </button>

      {/* Breadcrumb */}
      <div className="flex-1 overflow-hidden">
        <Breadcrumb />
      </div>

      {/* Right-side actions */}
      <div className="flex items-center gap-1">
        <ThemeMenu />
        <NotificationsMenu />

        {/* Language selector stub */}
        <button
          type="button"
          disabled
          aria-label="Selector de idioma (próximamente)"
          className={cn(iconBtnCls, 'cursor-not-allowed opacity-50')}
        >
          <Globe className="h-4 w-4" aria-hidden="true" />
        </button>

        <UserMenu user={user} onLogout={logout} />
      </div>
    </header>
  )
}
